import Bugsnag from '@bugsnag/js';
import Head from 'next/head';
import { useEffect } from 'react';
import Link from 'next/link';
import DefaultLayout from 'components/layouts/DefaultLayout';
import { FormProvider } from 'react-hook-form';
import styled from 'styled-components';
import useFormWithErrorTracking from 'hooks/useFormWithErrorTracking';
import { useCheckEmailMutation, useResetPasswordMutation } from 'auth/mutations/passwordMutations';
import { useRouter } from 'next/router';
import { useUserDetails } from 'auth/mutations/userHooks';
import Input from 'components/ui/Input/Input';
import ErrorMessage from 'components/ui/ErrorMessage/ErrorMessage';
import Button from 'components/ui/Button/Button';
import boatsetterPaths from 'utils/boatsetterPaths';
import { mediaMSmMax } from '../../../src/app/helpers/breakpoints';
import { AuthWrapper, InputWrapper } from '../../../src/app/components/auth_flow/SharedComponents';

const ForgotPasswordPage = () => {
  const router = useRouter();

  const { isAuthenticated } = useUserDetails();

  useEffect(() => {
    if (isAuthenticated) router.push('/');
  }, [isAuthenticated]);

  const checkEmail = useCheckEmailMutation();
  const resetPassword = useResetPasswordMutation();

  const processing = checkEmail.isLoading || resetPassword.isLoading;

  const methods = useFormWithErrorTracking({
    mode: 'onChange',
    defaultValues: {
      email: '',
    },
  });
  const { handleSubmit, register, errors, watch, setError } = methods;

  const submit = async (values) => {
    const { email } = values;
    try {
      await checkEmail.mutateAsync(email);
    } catch (e) {
      Bugsnag.notify(e);
      setError('email', {
        type: 'manual',
        message: 'Email not found',
      });
      console.error(e);
    }
    try {
      await resetPassword.mutateAsync({
        email,
      });
      router.push({
        pathname: '/a/password/sent',
        query: {
          email,
        },
      });
    } catch (e) {
      Bugsnag.notify(e);
      console.error(e);
    }
  };

  return (
    <DefaultLayout path="/password/new" name="forgot_password" Page={ForgotPasswordPage} hideFooter hideNav white>
      <Head>
        <meta name="robots" content="noindex" />
      </Head>
      <Wrapper>
        <Heading>Reset your password</Heading>
        <Subtext>Please enter the email associated with your Boatsetter account.</Subtext>
        <AuthWrapper>
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(submit)}>
              <InputWrapper>
                <Input
                  placeholder="Email"
                  type="email"
                  name="email"
                  hasValue={!!watch('email')}
                  hasError={!!errors?.email}
                  inputRef={register({
                    required: 'Please enter email address',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,20}$/i,
                      message: 'Invalid email address',
                    },
                  })}
                />
                <ErrorMessage error={errors?.email} />
              </InputWrapper>

              <ButtonWrap>
                <Button type="submit" disabled={processing} loading={processing} text="RESET PASSWORD" />
              </ButtonWrap>

              <Link passHref href={boatsetterPaths.logIn} legacyBehavior>
                <Cancel>Cancel</Cancel>
              </Link>
            </form>
          </FormProvider>
        </AuthWrapper>
      </Wrapper>
    </DefaultLayout>
  );
};

const ButtonWrap = styled.div`
  padding-top: 10px;
`;

const Cancel = styled.a`
  margin-top: 30px;
  display: flex;
  font-weight: 500;
  ${mediaMSmMax} {
    text-align: center;
    justify-content: center;
  }
`;

const Heading = styled.h1`
  font-weight: 600;
  font-size: 24px;
  margin-bottom: 0;
  ${mediaMSmMax} {
    font-size: 20px;
  }
`;

const Subtext = styled.div`
  padding: 20px 0;
`;

const Wrapper = styled.div`
  padding-top: 90px;

  display: flex;
  flex-flow: column;
  width: 420px;
  margin: auto;

  ${mediaMSmMax} {
    padding: 70px 22px 0 22px;
    width: auto;
  }
`;

export default ForgotPasswordPage;
